import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Link } from 'react-router-dom';
import {
  FileText,
  Bug,
  MessageSquare,
  PlusCircle,
  Clock,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ItemType } from '@/types';

const typeConfig: Record<ItemType, { icon: typeof FileText; label: string; color: string }> = {
  requirement: { icon: FileText, label: 'Feature Requests', color: 'text-blue-500' },
  feedback: { icon: MessageSquare, label: 'Feedback', color: 'text-purple-500' },
  bug: { icon: Bug, label: 'Bugs', color: 'text-red-500' },
};

export function Home() {
  const { user } = useAuth();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['item-stats'],
    queryFn: () => apiClient.getItemStats(),
  });

  const { data: recentItems, isLoading: recentLoading } = useQuery({
    queryKey: ['recent-activity'],
    queryFn: () => apiClient.getRecentActivity(5),
  });

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {greeting()}, {user?.given_name || 'there'}!
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Welcome to the Product Portal. Submit feedback, report bugs, or request new features.
        </p>
        <div className="mt-4">
          <Link
            to="/items/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Submit New Item
          </Link>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(typeConfig).map(([type, config]) => {
          const Icon = config.icon;
          const count = stats?.by_type?.[type] || 0;
          return (
            <Link
              key={type}
              to={`/items?type=${type}`}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {config.label}
                  </p>
                  <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                    {statsLoading ? '-' : count}
                  </p>
                </div>
                <div className={clsx('p-3 rounded-full bg-gray-100 dark:bg-gray-700', config.color)}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Clock className="mr-2 h-5 w-5 text-gray-400" />
            Recent Activity
          </h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {recentLoading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : !Array.isArray(recentItems) || recentItems.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              No recent activity
            </div>
          ) : (
            recentItems.map((item) => {
              const config = typeConfig[item.type];
              const Icon = config.icon;
              return (
                <Link
                  key={item.id}
                  to={`/items/${item.id}`}
                  className="block px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start space-x-3">
                    <Icon className={clsx('h-5 w-5 mt-0.5', config.color)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.created_by_name} &middot;{' '}
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        item.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : item.status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      )}
                    >
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                </Link>
              );
            })
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/items"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            View all of your items &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
